import type { ReactNode } from 'react'

import { Avatar } from '@/components/ui/Avatar'
import { CompletionBar } from '@/components/ui/CompletionBar'
import { Drawer } from '@/components/ui/Drawer'
import type { CandidateProfile } from '@/features/recruiter/api/candidates.api'
import { useCandidateDetailQuery } from '@/features/recruiter/hooks/useCandidates'
import { formatCurrency, formatDate, humanizeEnum } from '@/utils/format'

interface CandidateDetailDrawerProps {
  show: boolean
  onClose: () => void
  /** Search-results call site: fetches the full profile on open. */
  studentProfileId?: number
  /** Applicant-list call site: candidate data is already loaded, no extra fetch. */
  candidate?: CandidateProfile
  footer?: ReactNode
}

export function CandidateDetailDrawer({
  show,
  onClose,
  studentProfileId,
  candidate: providedCandidate,
  footer,
}: CandidateDetailDrawerProps) {
  const { data: fetchedCandidate, isPending } = useCandidateDetailQuery(
    providedCandidate ? undefined : (show ? studentProfileId : undefined),
  )
  const candidate = providedCandidate ?? fetchedCandidate

  return (
    <Drawer show={show} onClose={onClose} title="Candidate Profile" width="lg">
      {!candidate ? (
        <div className="p-4 text-center text-muted">{isPending ? 'Loading…' : 'No profile.'}</div>
      ) : (
        <div className="d-flex flex-column h-100">
          <div className="p-3 flex-grow-1 overflow-auto">
            <div className="d-flex align-items-center gap-3 mb-3">
              <Avatar name={candidate.name} size="lg" imageUrl={candidate.photoUrl} />
              <div>
                <div className="d-flex align-items-center gap-2">
                  <p className="fw-semibold fs-16 mb-0">{candidate.name}</p>
                  <span className="badge bg-light text-dark border fs-11">{candidate.program}</span>
                </div>
                {candidate.headline && <p className="text-muted fs-13 mb-0">{candidate.headline}</p>}
              </div>
            </div>

            <CompletionBar
              percent={candidate.profileCompletionPercent}
              label="Profile completeness"
            />

            <div className="row g-2 my-3 fs-13">
              <div className="col-6">
                <span className="text-muted">Email</span>
                <p className="mb-0">{candidate.email}</p>
              </div>
              {candidate.phone && (
                <div className="col-6">
                  <span className="text-muted">Phone</span>
                  <p className="mb-0">{candidate.phone}</p>
                </div>
              )}
              <div className="col-6">
                <span className="text-muted">Location</span>
                <p className="mb-0">
                  {[candidate.currentCity, candidate.currentState].filter(Boolean).join(', ') || '—'}
                </p>
              </div>
              <div className="col-6">
                <span className="text-muted">Experience</span>
                <p className="mb-0">
                  {candidate.isFresher
                    ? 'Fresher'
                    : `${Math.round((candidate.totalExperienceMonths ?? 0) / 12)} yrs`}
                </p>
              </div>
              {candidate.currentCompany && (
                <div className="col-6">
                  <span className="text-muted">Current Company</span>
                  <p className="mb-0">{candidate.currentCompany}</p>
                </div>
              )}
              {candidate.expectedCtc != null && (
                <div className="col-6">
                  <span className="text-muted">Expected CTC</span>
                  <p className="mb-0">
                    {formatCurrency(candidate.expectedCtc, candidate.salaryCurrency ?? 'INR')}
                  </p>
                </div>
              )}
              {candidate.noticePeriodDays != null && (
                <div className="col-6">
                  <span className="text-muted">Notice Period</span>
                  <p className="mb-0">{candidate.noticePeriodDays} days</p>
                </div>
              )}
            </div>

            {candidate.careerSummary && (
              <div className="mb-3">
                <p className="fw-medium fs-13 mb-1">Summary</p>
                <p className="fs-13 text-muted mb-0">{candidate.careerSummary}</p>
              </div>
            )}

            {candidate.skills.length > 0 && (
              <div className="mb-3">
                <p className="fw-medium fs-13 mb-1">Skills</p>
                <div className="d-flex flex-wrap gap-1">
                  {candidate.skills.map((skill) => (
                    <span key={skill.name} className="badge bg-light text-dark border">
                      {skill.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {candidate.experiences.length > 0 && (
              <div className="mb-3">
                <p className="fw-medium fs-13 mb-1">Experience</p>
                {candidate.experiences.map((exp, idx) => (
                  <div key={idx} className="fs-13 mb-2">
                    <p className="mb-0 fw-medium">{exp.jobTitle}</p>
                    <p className="mb-0 text-muted">
                      {exp.companyName ?? 'Confidential'} · {formatDate(exp.startDate)} –{' '}
                      {exp.isCurrent ? 'Present' : formatDate(exp.endDate)}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {candidate.educations.length > 0 && (
              <div className="mb-3">
                <p className="fw-medium fs-13 mb-1">Education</p>
                {candidate.educations.map((edu, idx) => (
                  <div key={idx} className="fs-13 mb-2">
                    <p className="mb-0 fw-medium">
                      {edu.degree}
                      {edu.course ? ` · ${edu.course}` : ''}
                    </p>
                    <p className="mb-0 text-muted">
                      {edu.institutionName}
                      {edu.endYear ? ` · ${edu.endYear}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {candidate.certifications.length > 0 && (
              <div className="mb-3">
                <p className="fw-medium fs-13 mb-1">Certifications</p>
                <div className="d-flex flex-wrap gap-1">
                  {candidate.certifications.map((cert) => (
                    <span key={cert} className="badge bg-light text-dark border">
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {candidate.languages.length > 0 && (
              <div className="mb-3">
                <p className="fw-medium fs-13 mb-1">Languages</p>
                <div className="d-flex flex-wrap gap-1">
                  {candidate.languages.map((lang) => (
                    <span key={lang.name} className="badge bg-light text-dark border">
                      {lang.name}
                      {lang.proficiency ? ` (${humanizeEnum(lang.proficiency)})` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          {footer && <div className="p-3 border-top">{footer}</div>}
        </div>
      )}
    </Drawer>
  )
}
